import React, { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SparkleBurst, CharacterView, PromptCapsule, HeartIcon, JuicyMeter, CaptionChip, ConfettiRain } from "./fx";
import { HandIcon, StarIcon } from "../ui-icons";

// CheerMeter — tap to fill a joy meter while the friend bounces bigger and
// hearts/stars pop; the cheer word pops up each tap. Faithful port of iOS
// CityHelperCheerView (cheer = fast tap w/ stars, wave = waves).

const HEART_TINTS = ["#ff7391", "#ffb840", "#8ca6ff", "#73d98c"];

const StarGlyph = ({ color }) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2l2.9 6.2 6.8.8-5 4.6 1.3 6.7L12 17.9 5.9 21.3 7.2 14.6l-5-4.6 6.8-.8z" />
  </svg>
);

const CheerMeter = ({ payload, sounds, onDone }) => {
  const target = payload.taps || 12;
  const doneRef = useRef(false);
  const seqRef = useRef(0);
  const [taps, setTaps] = useState(0);
  const [pops, setPops] = useState([]);
  const [word, setWord] = useState(0);
  const [bounce, setBounce] = useState(false);
  const [done, setDone] = useState(false);
  const [sparkle, setSparkle] = useState(0);
  const [confetti, setConfetti] = useState(0);

  const progress = taps / target;

  const cheer = useCallback(
    (e) => {
      if (done) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const nextTaps = taps + 1;
      setTaps(nextTaps);
      sounds.playPop(0.5 + progress * 0.5);
      seqRef.current += 1;
      const id = seqRef.current;
      const star = payload.style === "cheer" ? Math.random() > 0.5 : false;
      const tint = HEART_TINTS[Math.floor(Math.random() * HEART_TINTS.length)];
      setPops((prev) => [...prev, { id, x: Math.min(94, Math.max(6, x)), star, tint }]);
      window.setTimeout(() => setPops((prev) => prev.filter((p) => p.id !== id)), 1050);
      setWord((w) => w + 1);
      setBounce(true);
      window.setTimeout(() => setBounce(false), 150);
      if (nextTaps >= target && !doneRef.current) {
        doneRef.current = true;
        setDone(true);
        sounds.playPop(0.95);
        sounds.playCelebrate();
        setSparkle((s) => s + 1);
        setConfetti((c) => c + 1);
        window.setTimeout(() => onDone(), 1400);
      }
    },
    [done, onDone, payload.style, progress, sounds, taps, target]
  );

  return (
    <div className="ch-activity-body ch-cheer">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div className="ch-cheer-stage" onPointerDown={cheer}>
        <span
          className="ch-cheer-glow"
          style={{ opacity: 0.1 + progress * 0.3 }}
        />

        {pops.map((p) => (
          <motion.span
            key={p.id}
            className="ch-cheer-pop"
            style={{ left: `${p.x}%` }}
            initial={{ bottom: "24%", opacity: 1, scale: 1, rotate: 0 }}
            animate={{ bottom: "82%", opacity: 0, scale: 0.6, rotate: p.id % 2 ? 22 : -22 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            {p.star ? <StarGlyph color={p.tint} /> : <HeartIcon color={p.tint} size={26} />}
          </motion.span>
        ))}

        <motion.div
          className="ch-cheer-character"
          animate={{
            scaleX: (1 + progress * 0.14) * (bounce ? 1.07 : 1),
            scaleY: (1 + progress * 0.14) * (bounce ? 0.92 : 1),
          }}
          transition={{ type: "spring", stiffness: 400, damping: 12 }}
        >
          <CharacterView character={payload.character} happy={done} />
          <SparkleBurst trigger={sparkle} reach={130} style={{ left: "50%", top: "16%" }} />
        </motion.div>

        <AnimatePresence>
          {word > 0 && (
            <motion.span
              key={word}
              className="ch-cheer-word"
              initial={{ scale: 0.4, opacity: 0, y: 10, rotate: 0 }}
              animate={{ scale: 1, opacity: 1, y: 0, rotate: word % 2 ? -5 : 5 }}
              exit={{ scale: 0.6, opacity: 0, y: -20 }}
              transition={{ type: "spring", stiffness: 300, damping: 16 }}
            >
              {payload.cheerWord}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <JuicyMeter
        progress={progress}
        tint="#ff7a5c"
        icon={payload.style === "wave" ? <HandIcon size={20} /> : <StarIcon size={20} />}
        full={done}
      />

      <CaptionChip tone={done ? "done" : "active"}>
        {done ? "Hooray!" : payload.style === "wave" ? "Tap to wave!" : "Tap to cheer!"}
      </CaptionChip>

      <ConfettiRain trigger={confetti} count={22} />
    </div>
  );
};

export default CheerMeter;
